import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { languages, levels } from '../data/mock';
import { 
  ArrowLeft, 
  User, 
  BookOpen, 
  Target, 
  TrendingUp, 
  Clock,
  Star,
  Calendar,
  Award,
  Settings,
  Languages,
  Brain
} from 'lucide-react';

const Profile = () => {
  const navigate = useNavigate();
  const { progress, currentLanguage, currentLevel, getLanguageInfo } = useLanguage();
  const { user, logout, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });

  const currentLangInfo = getLanguageInfo(currentLanguage);
  const currentLevelInfo = levels.find(l => l.code === currentLevel);

  const handleSaveProfile = async () => {
    const result = await updateProfile(editForm);
    if (result.success) {
      setIsEditing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const achievements = [
    { 
      id: 1, 
      name: 'Erste Schritte', 
      description: 'Erste Geschichte gelesen',
      icon: '🎯',
      completed: true,
      date: '2024-01-15'
    },
    { 
      id: 2, 
      name: 'Wortsammler', 
      description: '25 Wörter gelernt',
      icon: '📚',
      completed: true,
      date: '2024-01-20'
    },
    { 
      id: 3, 
      name: 'Ausdauer', 
      description: '7 Tage Streak',
      icon: '🔥',
      completed: true,
      date: '2024-01-22'
    },
    { 
      id: 4, 
      name: 'Geschichtenerzähler', 
      description: '10 Geschichten gelesen',
      icon: '📖',
      completed: false,
      progress: 30
    }
  ];

  const weeklyStats = [
    { day: 'Mo', minutes: 12, stories: 1 },
    { day: 'Di', minutes: 8, stories: 0 },
    { day: 'Mi', minutes: 15, stories: 2 },
    { day: 'Do', minutes: 0, stories: 0 },
    { day: 'Fr', minutes: 20, stories: 1 },
    { day: 'Sa', minutes: 18, stories: 1 },
    { day: 'So', minutes: 10, stories: 1 }
  ];

  const maxMinutes = Math.max(...weeklyStats.map(s => s.minutes));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl font-bold">Mein Profil</h1>
            </div>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm">
                <Settings className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Profile */}
          <div className="lg:col-span-2 space-y-6">
            {/* User Info */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center space-x-6">
                  <div className="w-20 h-20 bg-[#4ECDC4] rounded-full flex items-center justify-center">
                    {user?.profile?.avatar ? (
                      <img src={user.profile.avatar} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <User className="h-10 w-10 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    {isEditing ? (
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="name">Name</Label>
                          <Input
                            id="name"
                            value={editForm.name}
                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">E-Mail</Label>
                          <Input
                            id="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveProfile}>Speichern</Button>
                          <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>Abbrechen</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <h2 className="text-2xl font-bold text-slate-900">
                            {user?.name || 'Deutschlerner'}
                          </h2>
                          <Button size="sm" variant="ghost" onClick={() => setIsEditing(true)}>
                            Bearbeiten
                          </Button>
                        </div>
                        <div className="text-sm text-slate-600 mb-2">{user?.email}</div>
                        <div className="flex items-center space-x-4 text-slate-600">
                          <div className="flex items-center space-x-2">
                            <Languages className="h-4 w-4" />
                            <span>{currentLangInfo.name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Target className="h-4 w-4" />
                            <span>Niveau {currentLevel}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4" />
                            <span>Seit {user?.profile?.joinDate ? new Date(user.profile.joinDate).toLocaleDateString('de-DE') : 'Januar 2024'}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <BookOpen className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{progress.completedStories.length}</div>
                  <div className="text-sm text-slate-600">Geschichten</div>
                </CardContent>
              </Card>

              <Card className="text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <Target className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{progress.learnedWords}</div>
                  <div className="text-sm text-slate-600">Wörter gelernt</div>
                </CardContent>
              </Card>

              <Card className="text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{progress.totalReadingTime}</div>
                  <div className="text-sm text-slate-600">Minuten</div>
                </CardContent>
              </Card>

              <Card className="text-center border-0 shadow-lg">
                <CardContent className="p-6">
                  <Star className="h-8 w-8 text-[#4ECDC4] mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{progress.streak}</div>
                  <div className="text-sm text-slate-600">Tage Streak</div>
                </CardContent>
              </Card>
            </div>

            {/* Weekly Activity */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#4ECDC4]" />
                  Wöchentliche Aktivität
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-end h-32">
                    {weeklyStats.map((stat, index) => (
                      <div key={index} className="flex flex-col items-center space-y-2">
                        <div className="flex flex-col items-center justify-end h-20">
                          <div 
                            className="w-8 bg-[#4ECDC4] rounded-t transition-all"
                            style={{ 
                              height: `${stat.minutes > 0 ? Math.max((stat.minutes / maxMinutes) * 100, 8) : 0}%` 
                            }}
                          />
                        </div>
                        <div className="text-sm font-medium text-slate-600">{stat.day}</div>
                        <div className="text-xs text-slate-500">{stat.minutes}min</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-center text-sm text-slate-600">
                    Diese Woche: {weeklyStats.reduce((acc, stat) => acc + stat.minutes, 0)} Minuten gelesen
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Language Progress */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Languages className="h-5 w-5 text-[#4ECDC4]" />
                  Sprachfortschritt
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{currentLangInfo.flag}</span>
                        <div>
                          <div className="font-medium text-slate-900">{currentLangInfo.name}</div>
                          <div className="text-sm text-slate-600">Aktuelles Niveau: {currentLevel}</div>
                        </div>
                      </div>
                      <Badge className="bg-[#4ECDC4] text-white">
                        {currentLevelInfo?.name}
                      </Badge>
                    </div>
                    <Progress value={65} className="h-2" />
                    <div className="flex justify-between text-xs text-slate-600 mt-1">
                      <span>A1</span>
                      <span>C1</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Schnellzugriff</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full bg-[#4ECDC4] hover:bg-[#45b7aa] text-white"
                  onClick={() => navigate('/stories')}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Geschichten lesen
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/training')}
                >
                  <Target className="h-4 w-4 mr-2" />
                  Vokabeln trainieren
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/admin')}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Admin Panel
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/admin/grammar')}
                >
                  <Brain className="h-4 w-4 mr-2" />
                  Grammatik verwalten
                </Button>
              </CardContent>
            </Card>

            {/* Achievements */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-[#4ECDC4]" />
                  Erfolge
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {achievements.map((achievement) => (
                    <div key={achievement.id} className="flex items-start space-x-3">
                      <div className={`text-2xl ${achievement.completed ? '' : 'grayscale'}`}>
                        {achievement.icon}
                      </div>
                      <div className="flex-1">
                        <div className={`font-medium ${achievement.completed ? 'text-slate-900' : 'text-slate-500'}`}>
                          {achievement.name}
                        </div>
                        <div className="text-sm text-slate-600">
                          {achievement.description}
                        </div>
                        {achievement.completed && achievement.date && (
                          <div className="text-xs text-slate-500 mt-1">
                            {new Date(achievement.date).toLocaleDateString('de-DE')}
                          </div>
                        )}
                        {!achievement.completed && achievement.progress && (
                          <div className="mt-2">
                            <Progress value={achievement.progress} className="h-1" />
                            <div className="text-xs text-slate-500 mt-1">
                              {achievement.progress}% abgeschlossen
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Level Info */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Aktuelles Niveau</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#4ECDC4] to-[#45b7aa] rounded-full flex items-center justify-center mx-auto">
                    <span className="text-2xl font-bold text-white">{currentLevel}</span>
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">{currentLevelInfo?.name}</div>
                    <div className="text-sm text-slate-600">{currentLevelInfo?.description}</div>
                  </div>
                  <Progress value={65} className="h-2" />
                  <div className="text-xs text-slate-600">
                    Fortschritt zu {levels[levels.findIndex(l => l.code === currentLevel) + 1]?.code || 'Abschluss'}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;